import { useEffect, useState } from 'react';
import { useQuery } from "@tanstack/react-query";

import Log from '@/Logger';
import { isDeepEqual } from '@/util';

import { useDeviceDisplayName } from './useDeviceDisplayName';
import { getTenantDevices } from '@/feature/api-client';

/**
 * デバイスの情報を表す型です
 * @property deviceId - RSI IoTで管理する内部デバイス識別子
 * @property serialId - デバイスの機番
 * @property name - デバイス表示名
 */
export type DeviceInfo = {
    deviceId: string;
    serialId: string;
    name: string;
};

/**
 * RSIデータ認証基盤に登録されているデバイス情報を取得するカスタムフック
 * @returns {DeviceInfo[]} - デバイス情報の配列
 * @throws {Error} - APIリクエストが失敗した場合 
 */
export const useDeviceList = () => {
    const { data, error, isLoading, isFetching, refetch } = useQuery({
        queryKey: ['DeviceList'],
        queryFn: getTenantDevices,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
    });
    const { getDisplayName, isLoading: isMappingLoading } = useDeviceDisplayName();
    const [deviceList, setDeviceList] = useState<DeviceInfo[]>([]);

    useEffect(() => {
        setDeviceList((prev: DeviceInfo[]) => {
            const logger = Log.getLogger();
            if (!data) return prev.length === 0 ? prev : [];
            const next = data.map(({ deviceId, serialId }) => ({ deviceId, serialId, name: getDisplayName(serialId) }));
            if (isDeepEqual(prev, next)) return prev;
            logger.ui.info('useDeviceList', 'fetchDeviceList', `device count: ${next.length}`);
            return next;
        });
    }, [data, getDisplayName]);

    return {
        deviceList,
        refetch,
        isLoading: isLoading || isMappingLoading,
        isFetching,
        error,
    };
};
